import { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Wallet, Eye, EyeOff, Sparkles, TrendingUp, Target } from "lucide-react";

function GoogleIcon() {
    return (
        <svg viewBox="0 0 24 24" className="w-4 h-4" aria-hidden="true">
            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
        </svg>
    );
}

export default function Login() {
    const { login, loginWithGoogle, resetPassword } = useAuth();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [show, setShow] = useState(false);
    const [busy, setBusy] = useState(false);
    const [googleBusy, setGoogleBusy] = useState(false);
    const [showForgot, setShowForgot] = useState(false);
    const [forgotEmail, setForgotEmail] = useState("");
    const [forgotBusy, setForgotBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!email || !password) { toast.error("Enter email and password"); return; }
        try {
            setBusy(true);
            await login(email, password);
            // No navigate() needed — PublicOnly in App.js redirects to "/dashboard" once user is set
        } catch (err) {
            const msg = err.message || "";
            if (msg.toLowerCase().includes("email not confirmed")) {
                toast.error("Please verify your email before signing in. Check your inbox.");
            } else {
                toast.error(msg || "Sign in failed");
            }
        } finally { setBusy(false); }
    };

    const handleGoogle = async () => {
        try {
            setGoogleBusy(true);
            await loginWithGoogle();
            // Supabase redirects — no navigate needed
        } catch (err) {
            toast.error(err.message || "Google sign-in failed");
            setGoogleBusy(false);
        }
    };

    const handleForgot = async (e) => {
        e.preventDefault();
        if (!forgotEmail) { toast.error("Enter your email"); return; }
        try {
            setForgotBusy(true);
            await resetPassword(forgotEmail);
            toast.success("Password reset email sent — check your inbox");
            setShowForgot(false);
        } catch (err) {
            toast.error(err.message || "Failed to send reset email");
        } finally { setForgotBusy(false); }
    };

    const useDemo = () => {
        setEmail("demo@budgetwise.app");
        setPassword("demo1234");
    };

    return (
        <div className="min-h-screen grid lg:grid-cols-2">
            {/* Left form panel */}
            <div className="flex flex-col justify-center px-6 sm:px-12 lg:px-20 py-12 bg-white">
                {/* Plain <a>, not <Link> — "/" is the static marketing landing page served
                    directly by nginx (frontend/public/landing.html), not an app route, so
                    this needs a full page navigation rather than client-side routing. */}
                <a href="/" className="flex items-center gap-2.5 mb-12">
                    <span className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#93C144] to-[#66882B] grid place-items-center text-white">
                        <Wallet className="w-5 h-5" />
                    </span>
                    <span className="font-display font-extrabold text-xl tracking-tight text-slate-900">BudgetWise</span>
                </a>

                <div className="max-w-md w-full">
                    {!showForgot ? (
                        <>
                            <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900 mb-2">Welcome back</h1>
                            <p className="text-slate-500 mb-8">Sign in to keep your money in flow.</p>

                            {/* Google OAuth */}
                            <Button
                                type="button" onClick={handleGoogle} disabled={googleBusy}
                                variant="outline"
                                className="w-full h-12 rounded-xl border-slate-200 font-semibold text-slate-700 hover:bg-slate-50 mb-4 gap-2.5">
                                <GoogleIcon />
                                {googleBusy ? "Redirecting…" : "Continue with Google"}
                            </Button>

                            <div className="flex items-center gap-3 mb-4">
                                <div className="flex-1 h-px bg-slate-200" />
                                <span className="text-xs text-slate-400 font-medium">or</span>
                                <div className="flex-1 h-px bg-slate-200" />
                            </div>

                            <form onSubmit={submit} className="space-y-4" data-testid="login-form">
                                <div className="space-y-1.5">
                                    <Label className="text-xs font-semibold text-slate-700">Email</Label>
                                    <Input data-testid="login-email" type="email" autoComplete="email"
                                        value={email} onChange={e => setEmail(e.target.value)}
                                        placeholder="you@example.com" className="h-12 rounded-xl" />
                                </div>
                                <div className="space-y-1.5">
                                    <Label className="text-xs font-semibold text-slate-700">Password</Label>
                                    <div className="relative">
                                        <Input data-testid="login-password" type={show ? "text" : "password"} autoComplete="current-password"
                                            value={password} onChange={e => setPassword(e.target.value)}
                                            placeholder="••••••••" className="h-12 rounded-xl pr-11" />
                                        <button type="button" onClick={() => setShow(!show)}
                                            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" tabIndex={-1}>
                                            {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                        </button>
                                    </div>
                                </div>
                                <Button type="submit" disabled={busy} data-testid="login-submit"
                                    className="w-full h-12 bg-[#83AC39] hover:bg-[#66882B] text-white rounded-xl font-semibold">
                                    {busy ? "Signing in…" : "Sign in"}
                                </Button>
                            </form>

                            <button type="button" onClick={useDemo} data-testid="use-demo"
                                className="mt-3 w-full h-12 rounded-xl border border-slate-200 text-sm font-semibold text-slate-700 hover:bg-slate-50 transition-all flex items-center justify-center gap-2">
                                <Sparkles className="w-4 h-4 text-[#83AC39]" /> Try the demo account
                            </button>

                            <p className="mt-6 text-sm text-slate-500 text-center">
                                <button type="button" onClick={() => { setForgotEmail(email); setShowForgot(true); }}
                                    className="text-[#83AC39] hover:text-[#66882B] font-medium">
                                    Forgot password?
                                </button>
                                <span className="mx-2 text-slate-300">·</span>
                                New to BudgetWise?{" "}
                                <Link to="/signup" className="text-[#83AC39] hover:text-[#66882B] font-semibold" data-testid="goto-signup">
                                    Create an account
                                </Link>
                            </p>
                        </>
                    ) : (
                        <>
                            <h1 className="font-display text-3xl font-extrabold tracking-tight text-slate-900 mb-2">Reset password</h1>
                            <p className="text-slate-500 mb-8">Enter your email and we'll send a reset link.</p>
                            <form onSubmit={handleForgot} className="space-y-4">
                                <div className="space-y-1.5">
                                    <Label className="text-xs font-semibold text-slate-700">Email</Label>
                                    <Input type="email" value={forgotEmail} onChange={e => setForgotEmail(e.target.value)}
                                        placeholder="you@example.com" className="h-12 rounded-xl" />
                                </div>
                                <Button type="submit" disabled={forgotBusy}
                                    className="w-full h-12 bg-[#83AC39] hover:bg-[#66882B] text-white rounded-xl font-semibold">
                                    {forgotBusy ? "Sending…" : "Send reset link"}
                                </Button>
                            </form>
                            <button type="button" onClick={() => setShowForgot(false)}
                                className="mt-4 w-full text-sm text-slate-500 hover:text-slate-700">
                                ← Back to sign in
                            </button>
                        </>
                    )}
                </div>
            </div>

            {/* Right hero panel */}
            <div className="hidden lg:flex relative items-center justify-center bg-slate-50 overflow-hidden"
                style={{ backgroundImage: 'radial-gradient(at 20% 20%, rgba(147,193,68,0.14) 0px, transparent 40%), radial-gradient(at 80% 30%, rgba(98,162,87,0.14) 0px, transparent 40%), radial-gradient(at 60% 90%, rgba(207,167,74,0.12) 0px, transparent 40%)' }}>
                <div className="absolute inset-0 opacity-[0.03] bg-[radial-gradient(circle_at_1px_1px,_rgba(0,0,0,0.5)_1px,_transparent_0)] [background-size:24px_24px]" />
                <div className="relative max-w-md p-10 space-y-6">
                    <div className="card-elev rounded-3xl p-7 animate-fade-up">
                        <div className="flex items-center justify-between mb-3">
                            <span className="text-xs font-bold uppercase tracking-[0.16em] text-slate-500">Total balance</span>
                            <span className="w-9 h-9 rounded-xl bg-[#F6FAF4] text-[#518948] grid place-items-center"><TrendingUp className="w-4 h-4" /></span>
                        </div>
                        <div className="font-display text-4xl font-extrabold tracking-tight text-slate-900">$31,882.00</div>
                        <div className="mt-1 text-sm text-[#518948] font-semibold">+12.4% this month</div>
                    </div>
                    <div className="card-elev rounded-3xl p-7 animate-fade-up delay-100">
                        <div className="flex items-center justify-between mb-3">
                            <span className="text-xs font-bold uppercase tracking-[0.16em] text-slate-500">Japan trip</span>
                            <span className="w-9 h-9 rounded-xl bg-[#F6FBEF] text-[#83AC39] grid place-items-center"><Target className="w-4 h-4" /></span>
                        </div>
                        <div className="font-display text-2xl font-bold text-slate-900">$2,150 <span className="text-base font-medium text-slate-400">/ $6,000</span></div>
                        <div className="mt-3 h-2 bg-slate-100 rounded-full overflow-hidden">
                            <div className="h-full bg-gradient-to-br from-[#93C144] to-[#66882B] rounded-full" style={{ width: "36%" }} />
                        </div>
                    </div>
                    <div className="card-elev rounded-3xl p-7 animate-fade-up delay-200">
                        <div className="flex items-center justify-between mb-3">
                            <span className="text-xs font-bold uppercase tracking-[0.16em] text-slate-500">Active subscriptions</span>
                        </div>
                        <div className="font-display text-2xl font-bold text-slate-900">9 services</div>
                        <div className="mt-1 text-sm text-slate-500">$158/mo · $1,896/yr</div>
                    </div>
                </div>
            </div>
        </div>
    );
}
